import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, Plus, Search } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";

type TargetSpecies = "carp" | "catfish" | "both";
type DirectoryLake = {
  id: string;
  canonical_name: string;
  venue_name?: string | null;
  county_region?: string | null;
  country?: string | null;
};
type PersonalLake = {
  id: number;
  name: string;
  canonical_lake_id?: string | null;
};

function lakeLabel(lake: DirectoryLake) {
  return lake.venue_name
    ? `${lake.canonical_name} · ${lake.venue_name}`
    : lake.canonical_name;
}

export default function StartFishingPage() {
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const movingSession = params.get("session_id");
  const fixedLakeId = params.get("lake_id");
  const requestedSpecies = params.get("species");

  const [directory, setDirectory] = useState<DirectoryLake[]>([]);
  const [personal, setPersonal] = useState<PersonalLake[]>([]);
  const [filter, setFilter] = useState("");
  const [canonicalId, setCanonicalId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [targetSpecies, setTargetSpecies] = useState<TargetSpecies>(
    requestedSpecies === "carp" || requestedSpecies === "catfish" || requestedSpecies === "both"
      ? requestedSpecies
      : "both",
  );
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [showAddLake, setShowAddLake] = useState(false);
  const [newLake, setNewLake] = useState({
    name: "",
    venue_name: "",
    postcode: "",
    location: "",
  });

  function openPegStep(lakeId: string | number, species: TargetSpecies, replace = false) {
    const mode = movingSession ? "&mode=move" : "&start_flow=1";
    const session = movingSession
      ? `&session_id=${encodeURIComponent(movingSession)}`
      : "";
    navigate(
      `/beta/les-quis-scout?lake_id=${encodeURIComponent(String(lakeId))}&species=${encodeURIComponent(species)}${session}${mode}`,
      { replace },
    );
  }

  useEffect(() => {
    if (!fixedLakeId) return;
    openPegStep(fixedLakeId, targetSpecies, true);
  }, [fixedLakeId]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch("/app-api/directory/search").then((response) => (response.ok ? response.json() : [])),
      fetch("/app-api/lakes").then((response) => (response.ok ? response.json() : [])),
    ])
      .then(([known, mine]) => {
        if (cancelled) return;
        setDirectory(Array.isArray(known) ? known : []);
        setPersonal(Array.isArray(mine) ? mine : []);
      })
      .catch(() => {
        if (!cancelled) setMessage("Could not load known waters. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const query = filter.trim().toLowerCase();
  const filtered = directory
    .filter((lake) => {
      if (!query) return false;
      return [lake.canonical_name, lake.venue_name, lake.county_region, lake.country]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    })
    .sort((a, b) => {
      const aName = a.canonical_name.toLowerCase();
      const bName = b.canonical_name.toLowerCase();
      const aStarts = aName.startsWith(query) ? 0 : 1;
      const bStarts = bName.startsWith(query) ? 0 : 1;
      return aStarts - bStarts || aName.localeCompare(bName);
    })
    .slice(0, 12);

  const selectedLake = directory.find((lake) => lake.id === canonicalId) ?? null;
  const showResults = pickerOpen && filter.trim().length > 0 && !selectedLake;

  function handleLakeSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setFilter(value);
    setCanonicalId("");
    setPickerOpen(true);
    setMessage("");
  }

  function selectLake(lake: DirectoryLake) {
    setCanonicalId(lake.id);
    setFilter(lakeLabel(lake));
    setPickerOpen(false);
    setMessage("");
  }

  async function resolveKnownLake() {
    if (!canonicalId || working) return;
    setWorking(true);
    setMessage("");
    try {
      let personalId = personal.find(
        (lake) => String(lake.canonical_lake_id ?? "") === canonicalId,
      )?.id;

      if (!personalId) {
        const response = await fetch("/app-api/directory/add-to-my-lakes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ canonical_lake_id: canonicalId }),
        });
        const data = await response.json();
        if (!response.ok || !data.id) {
          throw new Error(data.error || "Could not select this lake");
        }
        personalId = Number(data.id);
      }

      openPegStep(personalId, targetSpecies);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not select this lake");
    } finally {
      setWorking(false);
    }
  }

  async function addMissingLake(event: React.FormEvent) {
    event.preventDefault();
    if (!newLake.name.trim() || working) return;
    setWorking(true);
    setMessage("");
    try {
      const response = await fetch("/app-api/directory/submit-lake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newLake.name.trim(),
          venue_name: newLake.venue_name.trim() || null,
          postcode: newLake.postcode.trim() || null,
          location: newLake.location.trim() || null,
          species: targetSpecies,
          is_private: false,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.id) {
        throw new Error(data.error || "Could not add this lake");
      }
      openPegStep(data.id, targetSpecies);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not add this lake");
    } finally {
      setWorking(false);
    }
  }

  if (fixedLakeId) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8 text-sm text-secondary">
        Opening Scout &amp; Choose…
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-6 pb-28">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
          Start Fishing
        </p>
        <h1 className="mt-1 text-2xl font-bold text-primary">Let’s get you fishing</h1>
        <p className="mt-2 text-sm leading-6 text-secondary">
          Choose your lake and what you want to catch. We’ll help you with the rest.
        </p>
      </header>

      <p className="text-xs font-medium text-secondary">Step 1 of 3 · Choose your water</p>
      <div className="grid grid-cols-3 gap-2" aria-label="Start Fishing progress">
        <div className="rounded-lg border border-accent bg-accent/10 px-2 py-2 text-center text-xs font-semibold text-accent">
          1 · Water
        </div>
        <div className="rounded-lg border border-border bg-raised px-2 py-2 text-center text-xs font-semibold text-secondary">
          2 · Find a peg
        </div>
        <div className="rounded-lg border border-border bg-raised px-2 py-2 text-center text-xs font-semibold text-secondary">
          3 · Start fishing
        </div>
      </div>

      <section className="space-y-5 rounded-xl border border-border bg-raised p-4">
        <div className="relative">
          <label htmlFor="lake-filter" className="text-sm font-semibold text-primary">
            1. Which lake are you fishing?
          </label>
          <Input
            id="lake-filter"
            value={filter}
            onChange={handleLakeSearch}
            onFocus={() => setPickerOpen(true)}
            onBlur={() => setTimeout(() => setPickerOpen(false), 200)}
            placeholder={loading ? "Loading known waters…" : "Start typing a lake or fishery name…"}
            className="mt-2 border-2 border-accent/80 focus:border-accent"
            autoComplete="off"
            disabled={loading}
          />

          {showResults ? (
            <div className="absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-raised shadow-xl">
              {filtered.length ? (
                <>
                  <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-secondary">
                    Matching waters
                  </p>
                  {filtered.map((lake) => (
                    <button
                      key={lake.id}
                      type="button"
                      onMouseDown={() => selectLake(lake)}
                      className="flex w-full flex-col border-t border-border/50 px-3 py-2.5 text-left first:border-t-0 hover:bg-inset"
                    >
                      <span className="text-sm font-semibold text-primary">{lake.canonical_name}</span>
                      {(lake.venue_name || lake.county_region || lake.country) ? (
                        <span className="mt-0.5 text-xs text-secondary">
                          {[lake.venue_name, lake.county_region, lake.country]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </>
              ) : (
                <div className="px-3 py-3">
                  <p className="text-sm font-medium text-primary">No known water matches that search.</p>
                  <p className="mt-1 text-xs text-secondary">
                    Keep typing to refine it, or add the lake below if it is missing.
                  </p>
                </div>
              )}
            </div>
          ) : null}

          {selectedLake ? (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-success/50 bg-success-weak px-3 py-2.5">
              <Check className="mt-0.5 size-4 shrink-0 text-success" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-primary">{selectedLake.canonical_name}</p>
                {(selectedLake.venue_name || selectedLake.county_region) ? (
                  <p className="mt-0.5 text-xs text-secondary">
                    {[selectedLake.venue_name, selectedLake.county_region].filter(Boolean).join(" · ")}
                  </p>
                ) : null}
              </div>
            </div>
          ) : filter.trim() ? (
            <p className="mt-2 text-xs leading-5 text-secondary">
              Tap a matching water below the search box to select it.
            </p>
          ) : (
            <p className="mt-2 text-xs leading-5 text-secondary">
              Start typing, then tap your lake from the results.
            </p>
          )}
        </div>

        <div>
          <p className="text-sm font-semibold text-primary">2. What are you fishing for?</p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {(["carp", "catfish", "both"] as TargetSpecies[]).map((species) => (
              <button
                key={species}
                type="button"
                onClick={() => setTargetSpecies(species)}
                className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-semibold capitalize ${
                  targetSpecies === species
                    ? "border-accent bg-accent text-background"
                    : "border-border bg-background text-primary"
                }`}
              >
                {species}
              </button>
            ))}
          </div>
        </div>

        <Button
          size="lg"
          className="w-full font-bold"
          disabled={!canonicalId}
          isLoading={working && Boolean(canonicalId)}
          onClick={resolveKnownLake}
        >
          Continue <ChevronRight className="size-5" />
        </Button>

        <button
          type="button"
          onClick={() => {
            setShowAddLake((current) => !current);
            setNewLake((current) => ({ ...current, name: current.name || filter }));
          }}
          className="flex min-h-11 w-full items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-accent underline-offset-4 hover:underline"
        >
          <Plus className="size-4" /> Can't find your lake? Add it here
        </button>
      </section>

      {showAddLake ? (
        <form
          onSubmit={addMissingLake}
          className="space-y-3 rounded-xl border border-accent/40 bg-raised p-4"
        >
          <h2 className="font-semibold text-primary">Add this water</h2>
          <p className="text-sm text-secondary">
            Use it immediately. Admin will review it for the shared known-water list.
          </p>
          <Input
            value={newLake.name}
            onChange={(event) =>
              setNewLake((current) => ({ ...current, name: event.target.value }))
            }
            placeholder="Lake name *"
            required
          />
          <Input
            value={newLake.venue_name}
            onChange={(event) =>
              setNewLake((current) => ({ ...current, venue_name: event.target.value }))
            }
            placeholder="Fishery / venue (optional)"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              value={newLake.postcode}
              onChange={(event) =>
                setNewLake((current) => ({ ...current, postcode: event.target.value }))
              }
              placeholder="Postcode"
            />
            <Input
              value={newLake.location}
              onChange={(event) =>
                setNewLake((current) => ({ ...current, location: event.target.value }))
              }
              placeholder="Town / location"
            />
          </div>
          <Button type="submit" className="w-full" isLoading={working}>
            Add lake and continue
          </Button>
        </form>
      ) : null}

      {message ? (
        <p className="rounded-lg border border-error bg-error-weak p-3 text-sm text-error" role="alert">
          {message}
        </p>
      ) : null}
    </div>
  );
}
